import { ProductResult } from '../types/ProductResult';

/**
 * Check if a hit has a valid feed object structure
 * Reusable validation function
 */
export function hasValidFeed(hit: unknown): hit is { feed: Record<string, unknown> } {
  if (!hit || typeof hit !== 'object') return false;

  const hitObj = hit as Record<string, unknown>;
  if (!hitObj.feed || typeof hitObj.feed !== 'object') return false;

  return true;
}

/**
 * Extract product ID from hit
 * Priority: hit.id -> feed.id -> generate fallback
 */
function extractProductId(hit: Record<string, unknown>, feed: Record<string, unknown>): string | number {
  // Try hit.id first - must be string or number
  if (hit.id !== undefined && (typeof hit.id === 'string' || typeof hit.id === 'number')) {
    return hit.id;
  }

  // Try feed.id - must be string or number
  if (feed.id !== undefined && (typeof feed.id === 'string' || typeof feed.id === 'number')) {
    return feed.id;
  }

  // Generate fallback ID (timestamp + random)
  return `product-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Parse price string to number
 * Handles formats like "1 290 ₽", "1,290.50", etc.
 * Returns NaN if parsing fails
 */
function parsePriceString(priceStr: string): number {
  if (!priceStr || typeof priceStr !== 'string') {
    return NaN;
  }

  // Remove currency symbols, spaces, and replace comma with dot
  const cleaned = priceStr
    .replace(/[^\d,.\s]/g, '')  // Remove non-digit, comma, dot, space
    .replace(/\s/g, '')         // Remove spaces
    .replace(/,/g, '.');        // Replace comma with dot for decimal

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? NaN : parsed;
}

/**
 * Extract product title from feed
 * Priority: feed.name -> feed.title -> empty string
 */
function extractProductTitle(feed: Record<string, unknown>): string {
  if (typeof feed.name === 'string' && feed.name.trim()) {
    return feed.name.trim();
  }

  if (typeof feed.title === 'string' && feed.title.trim()) {
    return feed.title.trim();
  }

  // Return empty string for missing title (will cause normalizer to return null)
  return '';
}

/**
 * Normalize raw Meilisearch hit to ProductResult
 *
 * Rules:
 * 1. Extract data ONLY from hit.feed (as per task requirements)
 * 2. Return null if mandatory fields (id, title) are missing
 * 3. No UI logic, no side effects, no ranking
 * 4. Preserve raw hit reference
 *
 * @param hit Raw hit from Meilisearch response
 * @returns Normalized ProductResult or null if invalid
 */
export function productNormalizer(hit: unknown): ProductResult | null {
  // Validate hit has feed object
  if (!hasValidFeed(hit)) {
    return null;
  }

  const hitObj = hit as Record<string, unknown>;
  const feed = hitObj.feed as Record<string, unknown>;

  // Extract mandatory fields
  const id = extractProductId(hitObj, feed);
  const title = extractProductTitle(feed);

  // Title is mandatory - return null if empty
  if (!title) {
    return null;
  }

  // Extract optional fields from feed (only from feed, as required)
  const image = typeof feed.picture === 'string' ? feed.picture : undefined;
  const price = typeof feed.price === 'string' ? feed.price : undefined;
  const url = typeof feed.url === 'string' ? feed.url : undefined;

  // Extract availability
  const available = typeof feed.available === 'boolean' ? feed.available : undefined;

  // Parse numeric price for comparison
  const numericPrice = typeof price === 'string' ? parsePriceString(price) : undefined;

  // Handle old price logic
  let oldPrice: number | null | undefined = undefined;

  // Check if oldPrice exists in feed
  if (feed.oldPrice !== undefined && feed.oldPrice !== null) {
    const oldPriceValue = typeof feed.oldPrice === 'number' ? feed.oldPrice :
                         (typeof feed.oldPrice === 'string' ? parsePriceString(feed.oldPrice) : NaN);

    // Only include oldPrice if it's a valid number AND greater than current price
    if (!isNaN(oldPriceValue) && numericPrice !== undefined && oldPriceValue > numericPrice) {
      oldPrice = oldPriceValue;
    }
  }

  // Build base product result
  const productResult: any = {
    id,
    type: 'product',
    title,
    raw: hit, // Preserve reference to original hit
  };

  // Add optional fields only if they exist
  if (image !== undefined) productResult.image = image;
  if (price !== undefined) productResult.price = price;
  if (url !== undefined) productResult.url = url;
  if (available !== undefined) productResult.available = available;
  if (oldPrice !== undefined) productResult.oldPrice = oldPrice;
  if (numericPrice !== undefined) productResult.numericPrice = numericPrice;

  return productResult;
}